// src/pages/investor/TradingFloor.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import tradingService from '../../services/tradingService';
import { useToast } from '../../hooks/useToast';
import useTradingSocket from '../../hooks/useTradingSocket';
import DataTable from '../../components/common/DataTable';
import Button from '../../components/common/Button';
import { extractApiList, extractApiPagination } from '../../utils/responseAdapters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendUp, faArrowTrendDown, faChartLine, faXmark,
  faCartShopping, faReceipt, faCoins,
} from '@fortawesome/free-solid-svg-icons';

function formatPrice(p) {
  if (p == null) return '—';
  return '₹' + Number(p).toLocaleString('en-IN', { minimumFractionDigits: 2 });
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// ─── Order Form ───────────────────────────────────────────────────────────────

function OrderForm({ tokenId, onOrderPlaced }) {
  const { showToast } = useToast();
  const [side, setSide] = useState('buy');
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!price || !quantity || !tokenId) return;
    setSubmitting(true);
    try {
      await tradingService.createOrder({
        token_id: tokenId,
        side,
        price: Number(price),
        quantity: Number(quantity),
      });
      showToast(`${side.toUpperCase()} order placed`, 200);
      setPrice('');
      setQuantity('');
      onOrderPlaced?.();
    } catch {
      showToast('Failed to place order', 500);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{
      background: '#f9fafb', borderRadius: 12, padding: 20, marginBottom: 20,
    }}>
      <div style={{ display: 'flex', gap: 0, marginBottom: 16 }}>
        {['buy', 'sell'].map((s) => (
          <button key={s} type="button" onClick={() => setSide(s)} style={{
            flex: 1, padding: '8px 16px', border: 'none', cursor: 'pointer',
            fontWeight: 700, fontSize: 14, textTransform: 'uppercase',
            background: side === s ? (s === 'buy' ? '#10b981' : '#ef4444') : '#e5e7eb',
            color: side === s ? '#fff' : '#6b7280',
            borderRadius: s === 'buy' ? '8px 0 0 8px' : '0 8px 8px 0',
          }}>
            {s}
          </button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Price</label>
          <input type="number" min="0.01" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
            placeholder="Limit price" style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Quantity</label>
          <input type="number" min="1" step="1" value={quantity} onChange={(e) => setQuantity(e.target.value)}
            placeholder="Tokens" style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db' }} />
        </div>
      </div>
      {price && quantity && (
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
          Total: <strong>{formatPrice(Number(price) * Number(quantity))}</strong>
        </div>
      )}
      <Button variant="primary" type="submit" disabled={submitting || !price || !quantity}>
        {submitting ? 'Placing…' : `Place ${side.toUpperCase()} Order`}
      </Button>
    </form>
  );
}

// ─── Order Book ───────────────────────────────────────────────────────────────

function OrderBook({ tokenId }) {
  const [book, setBook] = useState({ bids: [], asks: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tokenId) return;
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const res = await tradingService.getOrderBook(tokenId);
        const data = res?.data?.data || res?.data;
        if (active) setBook({ bids: data?.bids || [], asks: data?.asks || [] });
      } catch {} finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [tokenId]);

  if (loading) return <p style={{ fontSize: 13, color: '#aaa' }}>Loading order book…</p>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {/* Bids (Buy) */}
      <div>
        <h4 style={{ margin: '0 0 8px', color: '#10b981', fontSize: 13 }}>BIDS (Buy)</h4>
        {book.bids.length === 0 ? <p style={{ fontSize: 12, color: '#aaa' }}>No bids</p> : (
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: '#9ca3af' }}>
              <th style={{ textAlign: 'left', padding: '4px 0' }}>Price</th>
              <th style={{ textAlign: 'right', padding: '4px 0' }}>Qty</th>
            </tr></thead>
            <tbody>
              {book.bids.slice(0, 10).map((b, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '4px 0', color: '#10b981', fontWeight: 600 }}>{formatPrice(b.price)}</td>
                  <td style={{ padding: '4px 0', textAlign: 'right' }}>{b.quantity || b.total_quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* Asks (Sell) */}
      <div>
        <h4 style={{ margin: '0 0 8px', color: '#ef4444', fontSize: 13 }}>ASKS (Sell)</h4>
        {book.asks.length === 0 ? <p style={{ fontSize: 12, color: '#aaa' }}>No asks</p> : (
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead><tr style={{ color: '#9ca3af' }}>
              <th style={{ textAlign: 'left', padding: '4px 0' }}>Price</th>
              <th style={{ textAlign: 'right', padding: '4px 0' }}>Qty</th>
            </tr></thead>
            <tbody>
              {book.asks.slice(0, 10).map((a, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '4px 0', color: '#ef4444', fontWeight: 600 }}>{formatPrice(a.price)}</td>
                  <td style={{ padding: '4px 0', textAlign: 'right' }}>{a.quantity || a.total_quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── My Orders ────────────────────────────────────────────────────────────────

function MyOrders({ refreshKey, onRefresh }) {
  const { showToast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const fetchOrders = useCallback(async (nextCursor = null) => {
    setLoading(true);
    try {
      const res = await tradingService.listMyOrders({ limit: 20, cursor: nextCursor || undefined });
      const data = res?.data?.data || res?.data;
      const items = extractApiList(data);
      const pagination = extractApiPagination(data) || data?.pagination;
      if (nextCursor) {
        setOrders((prev) => [...prev, ...items]);
      } else {
        setOrders(items);
      }
      setCursor(pagination?.next_cursor || null);
      setHasMore(pagination?.has_more || false);
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders, refreshKey]);

  const handleCancel = async (orderId) => {
    try {
      await tradingService.cancelOrder(orderId);
      showToast('Order cancelled', 200);
      fetchOrders();
      onRefresh?.();
    } catch {
      showToast('Failed to cancel order', 500);
    }
  };

  const columns = [
    { key: 'side', label: 'Side', render: (v) => (
      <span style={{ color: v === 'buy' ? '#10b981' : '#ef4444', fontWeight: 700, textTransform: 'uppercase' }}>{v}</span>
    )},
    { key: 'token_id', label: 'Token' },
    { key: 'price', label: 'Price', render: (v) => formatPrice(v) },
    { key: 'quantity', label: 'Qty' },
    { key: 'filled_quantity', label: 'Filled', render: (v) => v ?? 0 },
    { key: 'status', label: 'Status', render: (v) => (
      <span style={{
        background: v === 'open' ? '#f59e0b22' : v === 'filled' ? '#10b98122' : '#6b728022',
        color: v === 'open' ? '#f59e0b' : v === 'filled' ? '#10b981' : '#6b7280',
        padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600,
      }}>{v}</span>
    )},
    { key: 'created_at', label: 'Date', render: (v) => formatDate(v) },
  ];

  return (
    <div>
      <DataTable
        columns={columns}
        data={orders}
        loading={loading}
        emptyMessage="No orders yet."
        actions={(order) => order.status === 'open' ? (
          <button className="btn-icon" onClick={() => handleCancel(order.order_id || order.id)}>
            <FontAwesomeIcon icon={faXmark} style={{ color: '#ef4444' }} />
          </button>
        ) : null}
      />
      {hasMore && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
          <Button variant="secondary" onClick={() => fetchOrders(cursor)} disabled={loading}>Load More</Button>
        </div>
      )}
    </div>
  );
}

// ─── Trading Floor ────────────────────────────────────────────────────────────

export default function TradingFloor() {
  const { showToast } = useToast();
  const [holdings, setHoldings] = useState([]);
  const [selectedTokenId, setSelectedTokenId] = useState(null);
  const [tokenPrice, setTokenPrice] = useState(null);
  const [recentTrades, setRecentTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderRefreshKey, setOrderRefreshKey] = useState(0);

  // Real-time updates
  useTradingSocket({
    onPriceUpdate: (data) => {
      if (data.token_id === selectedTokenId) {
        setTokenPrice(data);
      }
    },
    onTradeExecuted: (data) => {
      if (data.token_id === selectedTokenId) {
        setRecentTrades((prev) => [data, ...prev.slice(0, 19)]);
      }
    },
  });

  // Load holdings (token list)
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await tradingService.getMyTradingHoldings();
        const items = extractApiList(res?.data?.data || res?.data);
        if (active) {
          setHoldings(items);
          if (items.length > 0 && !selectedTokenId) {
            setSelectedTokenId(items[0].token_id || items[0].id);
          }
        }
      } catch {} finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  // Load token price + recent trades when selection changes
  useEffect(() => {
    if (!selectedTokenId) return;
    let active = true;
    const load = async () => {
      try {
        const [priceRes, tradesRes] = await Promise.allSettled([
          tradingService.getTokenPrice(selectedTokenId),
          tradingService.getRecentTrades(selectedTokenId),
        ]);
        if (!active) return;
        if (priceRes.status === 'fulfilled') setTokenPrice(priceRes.value?.data?.data || priceRes.value?.data);
        if (tradesRes.status === 'fulfilled') setRecentTrades(extractApiList(tradesRes.value?.data?.data || tradesRes.value?.data));
      } catch {}
    };
    load();
    return () => { active = false; };
  }, [selectedTokenId]);

  const tradeColumns = [
    { key: 'price', label: 'Price', render: (v) => formatPrice(v) },
    { key: 'quantity', label: 'Qty' },
    { key: 'side', label: 'Side', render: (v) => (
      <span style={{ color: v === 'buy' ? '#10b981' : '#ef4444', fontWeight: 600, textTransform: 'uppercase' }}>{v}</span>
    )},
    { key: 'created_at', label: 'Time', render: (v) => formatDate(v) },
  ];

  return (
    <DashboardLayout>
      <div style={{ padding: '24px 20px' }}>
        <h2 style={{ marginTop: 0, marginBottom: 20 }}>
          <FontAwesomeIcon icon={faChartLine} style={{ marginRight: 10, color: '#2d8cf0' }} />
          Trading Floor
        </h2>

        {loading ? <p>Loading…</p> : holdings.length === 0 ? (
          <div style={{
            padding: 40, textAlign: 'center', background: '#f9fafb',
            borderRadius: 12, color: '#888',
          }}>
            <FontAwesomeIcon icon={faCoins} style={{ fontSize: 32, marginBottom: 12 }} />
            <div>No tradeable tokens. Acquire tokens through auctions first.</div>
          </div>
        ) : (
          <>
            {/* Token Selector */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, color: '#6b7280', marginBottom: 6, display: 'block' }}>Select Token</label>
              <select value={selectedTokenId || ''} onChange={(e) => setSelectedTokenId(e.target.value)}
                style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', minWidth: 260 }}>
                {holdings.map((h) => (
                  <option key={h.token_id || h.id} value={h.token_id || h.id}>
                    {h.token_symbol || h.art_title || h.token_id || h.id} — {h.quantity ?? h.sub_tokens_owned ?? 0} tokens
                  </option>
                ))}
              </select>
            </div>

            {/* Price Ticker */}
            {tokenPrice && (
              <div style={{
                display: 'flex', gap: 24, alignItems: 'center', marginBottom: 24,
                background: '#f9fafb', borderRadius: 12, padding: '16px 20px',
              }}>
                <div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>Last Price</div>
                  <div style={{ fontSize: 24, fontWeight: 800 }}>{formatPrice(tokenPrice.last_price || tokenPrice.price)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>24h Change</div>
                  <div style={{
                    fontSize: 16, fontWeight: 700,
                    color: (tokenPrice.change_24h || 0) >= 0 ? '#10b981' : '#ef4444',
                  }}>
                    <FontAwesomeIcon icon={(tokenPrice.change_24h || 0) >= 0 ? faArrowTrendUp : faArrowTrendDown}
                      style={{ marginRight: 6 }} />
                    {tokenPrice.change_24h != null ? `${tokenPrice.change_24h}%` : '—'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>24h Volume</div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{tokenPrice.volume_24h ?? '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>High / Low</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {formatPrice(tokenPrice.high_24h)} / {formatPrice(tokenPrice.low_24h)}
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
              {/* Left: Order Book + Order Form */}
              <div>
                <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>
                  <FontAwesomeIcon icon={faCartShopping} style={{ marginRight: 8 }} />
                  Order Book
                </h3>
                <OrderBook tokenId={selectedTokenId} />

                <h3 style={{ margin: '24px 0 12px', fontSize: 15 }}>Place Order</h3>
                <OrderForm
                  tokenId={selectedTokenId}
                  onOrderPlaced={() => setOrderRefreshKey((k) => k + 1)}
                />
              </div>

              {/* Right: Recent Trades */}
              <div>
                <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>
                  <FontAwesomeIcon icon={faReceipt} style={{ marginRight: 8 }} />
                  Recent Trades
                </h3>
                <DataTable
                  columns={tradeColumns}
                  data={recentTrades.slice(0, 20)}
                  emptyMessage="No trades yet."
                />
              </div>
            </div>

            {/* My Orders */}
            <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>
              <FontAwesomeIcon icon={faReceipt} style={{ marginRight: 8 }} />
              My Orders
            </h3>
            <MyOrders
              refreshKey={orderRefreshKey}
              onRefresh={() => setOrderRefreshKey((k) => k + 1)}
            />
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
